import React from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentCardFormContext } from '@cloudbeds/payment-tokenization';

import { ProcessorProps } from '../../@types';
import { SupportedProcessors } from '../../constants';
import { createProviderFactory } from '../../services';
import { useCommonOptionsStore } from '../../store';
// eslint-disable-next-line jest/no-mocks-import
import { generateFormConfigMock } from '../payment-methods/CreditCard/__mocks__';

export const Processor = ({ method, processors, methodConfig, billingConfig, options, children }: ProcessorProps) => {
  const locale = useCommonOptionsStore(state => state.lang);

  const { provider, getOptions } = React.useMemo(
    () => {
      if (SupportedProcessors.STRIPE === methodConfig!.processor) {
        return createProviderFactory(SupportedProcessors.STRIPE)
          .createProvider({
            publishableKey: processors.find(item => item.processor === SupportedProcessors.STRIPE)!.stripe?.clientKey,
            options,
            locale,
          });
      }

      return {};
    },
    [processors, methodConfig, options, locale],
  );

  if (methodConfig!.processor === SupportedProcessors.STRIPE) {
    return (
      <Elements options={getOptions(method)} stripe={provider}>
        {children}
      </Elements>
    );
  }

  if (methodConfig!.processor === SupportedProcessors.PAYPAL) {
    const elementOptions = {
      clientId: processors.find(item => item.processor === SupportedProcessors.PAYPAL)!.paypal!.clientId,
      currency: options!.currency.toUpperCase(),
      intent: 'capture',
    };

    return (
      <PayPalScriptProvider options={elementOptions}>
        {children}
      </PayPalScriptProvider>
    );
  }

  if (methodConfig!.processor === SupportedProcessors.DLOCAL) {
    const tokProcessor = methodConfig?.tokenization?.processor;
    if (!tokProcessor) return null; // TODO: should we care abou this?

    const {
      origin,
      timestamp,
      tokenexId: tokenExID,
      cardsSchema: tokenScheme,
      cardsAuthenticationKey: authenticationKey,
      cvvSchema: tokenSchemeCvv,
      cvvAuthenticationKey: authenticationKeyCvv,
    } = methodConfig.tokenization![tokProcessor];

    const tokenexConfig = {
      card: {
        origin,
        timestamp,
        tokenExID,
        tokenScheme,
        authenticationKey,
      },
      cvv: {
        origin,
        timestamp,
        tokenExID,
        tokenScheme: tokenSchemeCvv,
        authenticationKey: authenticationKeyCvv,
      },
    };

    const formConfig = {
      ...generateFormConfigMock(),
      billingFields: {
        ...billingConfig,
      },
    };

    return (
      <PaymentCardFormContext.Provider value={{
        tokenexConfig,
        formConfig,
      }}>
        {children}
      </PaymentCardFormContext.Provider>
    );
  }

  return (
    <>
      {children}
    </>
  );
};
